import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { COMMISSION_PRESETS } from '@/components/filters/date-range-presets';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPanel } from '@/components/ui/filter-panel';
import type { PayoutBatchesFilters as PayoutBatchesQueryFilters } from '@/hooks/queries/use-payout-batches-query';
import { usePayoutBatchesQuery } from '@/hooks/queries/use-payout-batches-query';
import { useQueryState, parseAsString } from 'nuqs';
import { Box } from '@chakra-ui/react';
import { MultiplePages, Search, WarningTriangle } from 'iconoir-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesTable } from './InvoicesTable';

const DEFAULT_LIMIT = 15;
const EMPTY_ICON_SIZE = 48;
const EMPTY_ICON_STROKE = 1.8;

export const InvoicesTabContent: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Use URL state for date range filter (reusing existing pattern)
  const [startDate] = useQueryState('invoicesStartDate', parseAsString);
  const [endDate] = useQueryState('invoicesEndDate', parseAsString);

  // Build filters with hasInvoice=true to only fetch batches whose payment was accepted
  const filters: PayoutBatchesQueryFilters = {
    hasInvoice: true,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate]);

  // Fetch data
  const {
    data: invoiceData,
    isLoading,
    isError,
  } = usePayoutBatchesQuery({
    ...filters,
    page: currentPage,
    limit: DEFAULT_LIMIT,
  });

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const invoices = invoiceData?.data ?? [];

  // `hasInvoice` is a fixed scope, not something the user picked: only the date
  // range can narrow the list, so it alone tells a filtered-out list apart from
  // a brand that has never been invoiced.
  const hasActiveFilters = Boolean(startDate || endDate);
  const isEmpty = !isLoading && !isError && invoices.length === 0;

  // Nothing to filter and nothing to page through: the date picker and the table
  // chrome come off screen together.
  const showFilterBar = !isEmpty || hasActiveFilters;

  return (
    <Box>
      {showFilterBar && (
        <FilterPanel mb={5}>
          <DateRangeFilter paramPrefix="invoices" presets={COMMISSION_PRESETS} showPresets={true} />
        </FilterPanel>
      )}
      {isError ? (
        <EmptyState
          title={t('commissions.invoices.errorLoading')}
          icon={<WarningTriangle width={22} height={22} />}
        />
      ) : isEmpty ? (
        <EmptyState
          icon={
            hasActiveFilters ? (
              <Search
                width={EMPTY_ICON_SIZE}
                height={EMPTY_ICON_SIZE}
                strokeWidth={EMPTY_ICON_STROKE}
              />
            ) : (
              <MultiplePages
                width={EMPTY_ICON_SIZE}
                height={EMPTY_ICON_SIZE}
                strokeWidth={EMPTY_ICON_STROKE}
              />
            )
          }
          title={t(
            hasActiveFilters ? 'commissions.invoices.noResults' : 'commissions.invoices.empty'
          )}
          description={t(
            hasActiveFilters
              ? 'commissions.invoices.noResultsDescription'
              : 'commissions.invoices.emptyDescription'
          )}
        />
      ) : (
        <InvoicesTable
          invoices={invoices}
          pagination={invoiceData?.meta}
          loading={isLoading}
          onPageChange={handlePageChange}
        />
      )}
    </Box>
  );
};

export default InvoicesTabContent;
